import React, { FunctionComponent } from "react";
import { MultiSelectChoices } from "../components/MultiSelectionChoices";
import { Ethnicity, Race } from "../../../@types";
import { useTranslate } from "../../../helpers";
import {
  createChoiceState,
  ethnicityOptions,
  raceOptions,
} from "../components/raceEthnicityHelpers";
import { ChoiceState } from "../components/types";

export const raceEthnicitySectionTestId = "race-ethnicity-section";
interface RaceEthnicitySectionProps {
  ethnicities: Ethnicity[];
  ethnicitiesError?: string;
  onEthnicitiesChanged: (updatedEthnicities: Ethnicity[]) => void;
  races: Race[];
  racesError?: string;
  onRacesChanged: (updatedRaces: Race[]) => void;
}

export const RaceEthnicitySection: FunctionComponent<RaceEthnicitySectionProps> = ({
  ethnicities,
  ethnicitiesError,
  onEthnicitiesChanged,
  races,
  racesError,
  onRacesChanged,
}: RaceEthnicitySectionProps) => {
  const t = useTranslate();

  const ethnicityChoices = createChoiceState(ethnicityOptions, ethnicities, t);

  const raceChoices = createChoiceState(raceOptions, races, t);

  const handleEthnicityChanged = (updatedChoices: ChoiceState[]) => {
    const updatedValues: Ethnicity[] = updatedChoices.reduce(
      (acc: Ethnicity[], choice: ChoiceState) => {
        if (choice.checked) {
          acc.push(choice.value as Ethnicity);
        }
        return acc;
      },
      []
    );
    onEthnicitiesChanged(updatedValues);
  };

  const handleRaceChanged = (updatedChoices: ChoiceState[]) => {
    const updatedValues: Race[] = updatedChoices.reduce(
      (acc: Race[], choice: ChoiceState) => {
        if (choice.checked) {
          acc.push(choice.value as Race);
        }
        return acc;
      },
      []
    );
    onRacesChanged(updatedValues);
  };

  return (
    <section data-testid={raceEthnicitySectionTestId}>
      <h2 className="ds-text-heading--lg">
        {t("enroll_form.race_ethnicity_section.title")}
      </h2>
      <p className="ds-text-body--md">
        {t("enroll_form.race_ethnicity_section.info")}
      </p>
      <div className="mct-c-personal-info__race-ethnicity">
        <MultiSelectChoices
          label={
            <span className="ds-u-text-transform--uppercase">
              {t("enroll_form.ethnicity_section.title")}
            </span>
          }
          hint={t("enroll_form.ethnicity_section.instruction")}
          initialChoices={ethnicityChoices}
          onSelectionChanged={handleEthnicityChanged}
          name="ethnicities"
          errorMessage={ethnicitiesError}
          optOutValue={Ethnicity.ETHNICITY_I_CHOOSE_NOT_TO_ANSWER}
        />
      </div>
      <div className="mct-c-personal-info__race-ethnicity">
        <MultiSelectChoices
          label={
            <span className="ds-u-text-transform--uppercase">
              {t("enroll_form.race_section.title")}
            </span>
          }
          hint={t("enroll_form.race_section.instruction")}
          errorMessage={racesError}
          initialChoices={raceChoices}
          onSelectionChanged={handleRaceChanged}
          name="races"
          optOutValue={Race.RACE_I_CHOOSE_NOT_TO_ANSWER}
        />
      </div>
    </section>
  );
};
